import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GrSearch } from 'react-icons/gr';
import { FaYinYang, FaShoppingCart } from 'react-icons/fa';
import Search from './Search';
import { useDispatch, useSelector } from 'react-redux';
import { setUserDetails } from '../store/userSlice';
import { selectCartCount } from '../store/cartSlice'
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import ROLE from '../common/role'
import { VERTICAL_META, PRODUCT_VERTICAL } from '../common/productVertical'

// Vertical shortcuts pinned at the top of the mobile drawer. Phase 6b of
// marketplace-V1 — mirrors the DepartmentTiles on the home page + the
// vertical pills on the desktop CategoryNav.
const MOBILE_VERTICAL_ORDER = [
  PRODUCT_VERTICAL.GROCERIES,
  PRODUCT_VERTICAL.FASHION,
  PRODUCT_VERTICAL.ELECTRONICS,
  PRODUCT_VERTICAL.EVENTS,
]

// MobileHeader is self-contained: it reads the user from Redux and performs its own
// logout. The previous design required the parent to pass an `onLogout` prop, which
// `Header.js` did not — so the mobile logout button silently did nothing in production.
const MobileHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const count = Number(useSelector(selectCartCount) || 0)
  const displayCount = count > 99 ? '99+' : count
  const user = useSelector(state => state?.user?.user)

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setIsSearchOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      const res = await fetch(SummaryApi.logout_user.url, {
        method: SummaryApi.logout_user.method,
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (data?.success || res.ok) {
        dispatch(setUserDetails(null))
        toast.success(data?.message || 'Logged out')
        navigate('/')
      } else {
        toast.error(data?.message || 'Failed to log out')
      }
    } catch (err) {
      toast.error('Failed to log out')
    } finally {
      toggleMenu()
    }
  }

  return (
    <header className="h-24 lg:hidden sticky top-0 z-40 flex flex-col justify-center gap-1 bg-white">
      <div className="container mx-auto flex items-center px-4 justify-between">
        <div className="flex items-center gap-4">
          <button
            className="text-2xl focus:outline-none"
            onClick={toggleSearch}
            aria-label="Search"
          >
            <GrSearch />
          </button>

          <button
            className="text-3xl cursor-pointer relative flex justify-center"
            onClick={toggleMenu}
            aria-label="Open menu"
          >
            {user?.profilePic ? (
              <img
                src={user.profilePic}
                className="w-10 h-10 rounded-full"
                alt={user.name}
              />
            ) : (
              <FaYinYang />
            )}
          </button>

          <Link to="/cart" className="text-2xl relative" aria-label="Cart">
            <span>
              <FaShoppingCart />
            </span>
            <div className="absolute -top-2 -right-2 bg-red-600 text-white h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center text-[10px] leading-none font-semibold">
              {displayCount}
            </div>
          </Link>
        </div>
      </div>

      {isMenuOpen && (
        <div className="absolute bg-white bottom-0 top-24 w-full p-4 shadow-lg overflow-y-auto">
          {/* Shop by Department — Phase 6b of marketplace-V1. Same four
              verticals as the desktop nav pills + home tiles. Grid layout
              stays finger-friendly on small screens. */}
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Shop by department
            </div>
            <div className="grid grid-cols-2 gap-2">
              {MOBILE_VERTICAL_ORDER.map((v) => {
                const meta = VERTICAL_META[v]
                if (!meta) return null
                return (
                  <Link
                    key={v}
                    to={meta.route}
                    onClick={toggleMenu}
                    className="flex items-center gap-2 p-3 bg-slate-50 hover:bg-slate-100 rounded border border-slate-200"
                  >
                    <span className="text-xl" aria-hidden="true">{meta.emoji}</span>
                    <span className="font-medium text-slate-800">{meta.plural}</span>
                  </Link>
                )
              })}
            </div>
          </div>

          <nav>
            {(user?.role === ROLE.SUPER_ADMIN || user?.role === ROLE.ADMIN || user?.role === ROLE.SUB_ADMIN) && (
              <>
                <Link
                  to="/admin-panel/all-products"
                  className="block hover:bg-slate-100 p-2"
                  onClick={toggleMenu}
                >
                  Admin Panel
                </Link>
                <Link
                  to="/tickets/scanner"
                  className="block hover:bg-slate-100 p-2"
                  onClick={toggleMenu}
                >
                  Ticket Scanner
                </Link>
              </>
            )}
            {user?._id && (
              <button
                className="block hover:bg-slate-100 p-2 w-full text-left"
                onClick={handleLogout}
              >
                Logout
              </button>
            )}
            {!user?._id && (
              <Link
                to="/login"
                className="block hover:bg-slate-100 p-2"
                onClick={toggleMenu}
              >
                Login
              </Link>
            )}
          </nav>
        </div>
      )}

      {isSearchOpen && (
        <div className="absolute bg-white bottom-0 top-24 w-full p-4 shadow-lg">
          <Search />
        </div>
      )}
    </header>
  );
};

export default MobileHeader;
